import { Directive, ElementRef, EventEmitter, HostListener, Output, inject } from '@angular/core';

/**
 * Directive that emits an event when a click occurs outside the host element.
 * Useful for closing dropdowns, modals, or other UI elements when clicking outside.
 *
 * @example
 * ```html
 * <div appClickOutside (clickOutside)="closeDropdown()">
 *   <!-- Dropdown content -->
 * </div>
 * ```
 */
@Directive({
  selector: '[appClickOutside]',
  standalone: true,
})
export class ClickOutsideDirective {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  /**
   * Event emitted when a click occurs outside the host element.
   */
  @Output() clickOutside = new EventEmitter<void>();

  /**
   * Listens to document clicks and checks if the click target is outside the host element.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target === null) {
      return;
    }

    const clickedInside: boolean = this.elementRef.nativeElement.contains(target);
    if (!clickedInside) {
      this.clickOutside.emit();
    }
  }
}
